import React, { ChangeEvent, RefObject, useEffect, useRef, useState } from 'react';
import { AvMessagePopover, ClearButton, InputTooltipWrapper, PopperComponent, SimplePopperComponent } from '@fabric-ui/components';
import { Attention, Plus, Search, X } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { abbreviateNumber, emptyArray, emptyObject, generateOption, noop, uniqBy } from '@fabric-ui/utils';
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Box,
  Button,
  ClickAwayListener,
  Divider,
  InputAdornment,
  TextField,
} from '@mui/material';
import { ClearSelection } from './ClearSelection';
import { AvalorSelectLoadingValue, DIVIDER_OPTION, FilterStyledPopper, popupIcon, showOnlyButtonStyle } from './constants';
import { MultiSelectPaper } from './MultiSelectPaper';
import { MultiSelectTags } from './MultiSelectTags';
import { SelectTrigger } from './SelectTrigger';
import { Option, OptionObject, SelectProps } from './types';
import { getDefaultValue, getRenderOptionFunc } from './utils';

const clearIcon = <X />;
const searchIcon = <Search />;
const plusIcon = <Plus />;
const noopEmptyObject = () => ({});

export function Select<T>({
  label,
  value: originalValue,
  onChange = noop,
  error = '',
  options = emptyArray,
  size = 'medium',
  acceptNullValue = false,
  disabled = false,
  disabledTooltipText,
  startAdornment,
  freeSolo,
  style = emptyObject,
  isMultiple = false,
  muiProps = emptyObject,
  getValueFunc = ({ value }: any) => value,
  getLabelFunc = ({ title }: any) => title || '',
  groupByFunc,
  filterFunc,
  isRequired = false,
  renderOption,
  placeholder = '',
  transparent = false,
  showSelection = true,
  loading = false,
  disableCloseOnSelect = false,
  showInput = true,
  selectIcon = popupIcon,
  hidePlaceHolder = false,
  missingValueErrorText = 'value missing from options',
  onClose,
  showOnlyAutoComplete = false,
  showClearSelection = false,
  onCreateNew,
  hasApplyButton = false,
  variant = 'input',
  type = '',
  renderTagSeparator,
  hideValue = false,
  shouldCloseOnChange,
  customActionButton,
  totalOptionsCount,
  renderOptionStyle = noopEmptyObject,
  skeletonLoading = false,
  valueAsObject = false,
  inputToolbar,
  filterPopperMinWidth = 'fit-content',
  emptyState,
  readOnly = false,
  showAllTags,
  displayErrorOnMultiply = false,
}: SelectProps<T>) {
  const buttonEl = useRef<HTMLButtonElement>(null);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [orderedOptions, setOrderedOptions] = useState(options);

  useEffect(() => {
    setOpen(isOpen);
  }, [options]);

  const [draftValue, setDraftValue] = useState<T | T[] | undefined | null>(isMultiple ? [] : undefined);
  const value = hasApplyButton ? draftValue : originalValue;

  const extractSingleValue = val => options.find(opt => getValueFunc(opt) === val) || null;
  const processedValue =
    freeSolo && !isMultiple && valueAsObject
      ? (value as OptionObject<T>)
      : value !== undefined
        ? isMultiple && Array.isArray(value)
          ? (value.map(extractSingleValue) as Option<T>[])
          : extractSingleValue(value)
        : getDefaultValue(isMultiple);

  const errorText = (
    isMultiple && Array.isArray(processedValue)
      ? processedValue.filter(val => !val).length || displayErrorOnMultiply
      : !processedValue && value !== undefined && (acceptNullValue || value !== null) && !freeSolo
  )
    ? missingValueErrorText
    : undefined;

  const doChange = (
    val: NonNullable<string | Option<T>> | (string | Option<T>)[] | null,
    reason: AutocompleteChangeReason,
    { option }: { option: Option<T> } | undefined = {} as { option: Option<T> }
  ) => {
    const calculatedVal = isMultiple
      ? ((Array.isArray(val) ? val : []) as OptionObject<T>[]).map(getValueFunc)
      : val
        ? getValueFunc(val as OptionObject<T>)
        : null;
    if (hasApplyButton) {
      return setDraftValue(calculatedVal);
    }
    return onChange(calculatedVal, val, reason, option);
  };

  const autocompleteProps = {
    className: isMultiple ? 'autocomplete-multiple' : undefined,
    popupIcon: readOnly ? <div /> : variant !== 'input' ? searchIcon : selectIcon,
    clearIcon,
    size,
    autoComplete: !freeSolo,
    loading,
    autoHighlight: !freeSolo,
    PopperComponent: variant !== 'input' ? SimplePopperComponent : PopperComponent,
    multiple: isMultiple,
    disableCloseOnSelect: isMultiple || disableCloseOnSelect,
    options: [...orderedOptions, ...(skeletonLoading && loading ? [generateOption(AvalorSelectLoadingValue)] : [])],
    getOptionLabel: getLabelFunc as any,
    getOptionKey: ((option: OptionObject<T>) => String(getValueFunc(option))) as any,
    groupBy: groupByFunc,
    filterOptions: filterFunc as any,
    isOptionEqualToValue: (option, val) => val === '' || getValueFunc(option) === getValueFunc(val),
    disableClearable: isRequired || variant === 'text',
    renderOption: getRenderOptionFunc({
      renderOption,
      isMultiple,
      getLabelFunc,
      renderOptionStyle,
      skeletonLoading,
      options: orderedOptions,
    }),
    freeSolo,
    sx: variant === 'input' ? style : undefined,
    readOnly,
    forcePopupIcon: freeSolo,
    blurOnSelect: !isMultiple,
    ...muiProps,
    componentsProps: { clearIndicator: { tabIndex: -1 }, ...muiProps?.componentsProps },
    onKeyDown: e => {
      e.stopPropagation();
      muiProps?.onKeyDown?.(e);
    },
  } as const;

  const getDisplayValue = val => (isMultiple ? val.map(getLabelFunc).join(', ') : getLabelFunc(val));

  const setOpen = (open: boolean) => {
    setIsOpen(open);
    if (open && (isMultiple || showClearSelection) && variant !== 'input' && !groupByFunc) {
      setOrderedOptions(
        uniqBy([...(isMultiple && Array.isArray(processedValue) ? processedValue : [processedValue]), ...options], getValueFunc)
      );
    } else if (!(isMultiple && variant === 'input')) {
      setOrderedOptions(options);
    }
  };

  const handleClick = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setSearch('');
    onClose?.();
    if (hasApplyButton) {
      setDraftValue([]);
    }
  };

  const onChangeAutocomplete = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: NonNullable<string | Option<T>> | (string | Option<T>)[] | null,
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails<Option<T>> | undefined
  ) => {
    if (event.type === 'keydown' && (event as any).key === 'Backspace' && reason === 'removeOption') {
      return;
    }
    doChange(newValue, reason, details);
    if (!autocompleteProps.disableCloseOnSelect || shouldCloseOnChange?.(newValue)) {
      setOpen(false);
    }
  };

  const hasValidValue = typeof value === 'boolean' || !!(value as T[])?.length || (acceptNullValue && value === null);
  const id = isOpen ? 'filter-mode' : undefined;
  const showPlaceholder = Boolean(placeholder && !processedValue);

  const filterAutoComplete = () => (
    <Box sx={{ zIndex: theme => theme.zIndex.modal }}>
      <Autocomplete
        onClose={(e, reason) => reason === 'escape' && handleClose()}
        open
        value={errorText ? getDefaultValue(isMultiple) : processedValue}
        onChange={onChangeAutocomplete}
        inputValue={search}
        onInputChange={(event, value, reason) => {
          if (!['reset', 'blur', 'selectOption', 'removeOption'].includes(reason)) {
            setSearch(value);
          }
        }}
        renderInput={params => (
          <>
            <TextField
              {...params}
              sx={{ display: showInput ? 'unset' : options.length > 5 || search ? 'unset' : 'none' }}
              autoFocus
              placeholder="Search"
              InputProps={{
                ...params.InputProps,
                ...(muiProps?.InputProps || {}),
                startAdornment: <Box sx={{ ...flex.row, color: 'var(--color-content-base-tertiary)' }}>{searchIcon}</Box>,
                ...(variant === 'text' && {
                  endAdornment: (
                    <ClearButton
                      onClick={args => params.inputProps.onChange?.(args as unknown as ChangeEvent<HTMLInputElement>)}
                      sx={{ position: 'absolute', right: '8px' }}
                    />
                  ),
                }),
              }}
            />
            {inputToolbar}
          </>
        )}
        {...autocompleteProps}
        popupIcon={false}
        sx={{ ...autocompleteProps.sx, mb: showInput ? '6px' : 0 }}
        size={size}
        noOptionsText={emptyState}
      />
      {((options.length > 2 && isMultiple) || showClearSelection || onCreateNew || hasApplyButton || customActionButton) && (
        <>
          <Divider sx={{ mt: '6px' }} />
          <Box sx={{ pt: '8px', pl: '4px', pr: 1, gap: 2, ...flex.justifyBetweenCenter }}>
            {customActionButton}
            {onCreateNew && (
              <Button
                onClick={() => {
                  onCreateNew();
                  handleClose();
                }}
                variant="primaryText">
                {plusIcon} Create New
              </Button>
            )}
            {!hasApplyButton && !customActionButton && ((isMultiple && options.length > 2) || showClearSelection) ? (
              <ClearSelection disabled={!(value as T[])?.length} onClick={() => onChange(isMultiple ? ([] as T[]) : ('' as T))} />
            ) : null}
            {!hasApplyButton && isMultiple ? (
              <Box sx={{ ...flex.itemsCenter, gap: 1, fontStyle: 'italic', color: 'var(--color-content-base-tertiary)' }}>
                {(value as Option<T>[])?.length || 0} / {abbreviateNumber(totalOptionsCount ?? options.length)} values
                {(totalOptionsCount as number) > options.length && (
                  <AvMessagePopover
                    asTooltip
                    icon={<Attention />}
                    includeIconInMessage={false}
                    message={`Showing first ${options.length} values, search to see more`}
                  />
                )}
              </Box>
            ) : null}
            {hasApplyButton && (
              <>
                {isMultiple ? (
                  <Box sx={{ width: 100, fontStyle: 'italic', fontSize: 12, color: 'var(--color-content-base-tertiary)' }}>
                    {(draftValue as Option<T>[])?.length}/{options.length} Selected
                  </Box>
                ) : null}
                <div>
                  <Button size="small" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    disabled={(draftValue as Option<T>[])?.length === 0}
                    variant="contained"
                    onClick={() => {
                      onChange(draftValue as any);
                      handleClose();
                    }}>
                    Apply
                  </Button>
                </div>
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
  const displayValue =
    hasValidValue && !errorText && showSelection && !hideValue
      ? `${getDisplayValue(Array.isArray(processedValue) ? [processedValue[0]] : processedValue)}`
      : '';
  const tooltipValue = hasValidValue && !errorText && showSelection ? `${getDisplayValue(processedValue)}` : '';

  return showOnlyAutoComplete ? (
    filterAutoComplete()
  ) : variant !== 'input' ? (
    <>
      <SelectTrigger
        ariaDescribedby={id}
        onClick={handleClick}
        size={size}
        disabled={disabled}
        disabledTooltipText={disabledTooltipText}
        open={isOpen}
        hasValidValue={hasValidValue}
        showPlaceholder={showPlaceholder}
        transparent={transparent}
        ref={buttonEl}
        placeholder={placeholder}
        label={label}
        processedValue={isMultiple && Array.isArray(processedValue) ? processedValue?.slice?.(1) : processedValue}
        chipCount={isMultiple && Array.isArray(processedValue) && processedValue?.length ? processedValue.length - 1 : undefined}
        displayValue={displayValue}
        showSelection={showSelection}
        tooltipValue={tooltipValue}
        // @ts-ignore
        showChipCount={!(variant !== 'input' && transparent && isMultiple) && typeof label === 'string'}
        selectIcon={selectIcon}
        type={type as 'button' | 'reset' | 'submit'}
        sx={style}
        variant={variant}
      />
      <FilterStyledPopper
        id={id}
        open={isOpen}
        anchorEl={buttonEl.current}
        placement="bottom-start"
        style={{ minWidth: filterPopperMinWidth, width: buttonEl.current?.clientWidth }}>
        <ClickAwayListener onClickAway={handleClose}>{filterAutoComplete()}</ClickAwayListener>
      </FilterStyledPopper>
    </>
  ) : (
    <Autocomplete
      value={errorText ? getDefaultValue(isMultiple) : processedValue}
      onChange={(_, ...params) => doChange(...params)}
      onOpen={() => {
        if (variant === 'input' && isMultiple) {
          setOrderedOptions(
            uniqBy(
              [
                ...(isMultiple && Array.isArray(processedValue) ? processedValue : [processedValue]),
                isMultiple ? { title: '', value: DIVIDER_OPTION } : null,
                ...options,
              ],
              getValueFunc
            )
          );
        }
      }}
      disabled={disabled}
      renderInput={params => (
        <InputTooltipWrapper
          error={!loading && (error || errorText)}
          inputNode={(params.inputProps.ref as RefObject<HTMLInputElement | null>)?.current}
          value={params.inputProps.value}>
          <TextField
            required={isRequired}
            {...params}
            label={label}
            error={Boolean((error || errorText) && !loading)}
            placeholder={readOnly || disabled || hidePlaceHolder ? undefined : placeholder}
            InputProps={{
              ...params.InputProps,
              ...(muiProps?.InputProps || {}),
              startAdornment: (
                <>
                  {params.InputProps.startAdornment}
                  {startAdornment && <InputAdornment position="start">{startAdornment(processedValue)}</InputAdornment>}
                </>
              ),
            }}
          />
        </InputTooltipWrapper>
      )}
      renderTags={
        isMultiple
          ? (tags, getTagProps) => (
              <MultiSelectTags
                tags={tags}
                size={size}
                showAllTags={showAllTags}
                readOnly={readOnly}
                disabled={disabled}
                getTagProps={getTagProps}
                getValueFunc={getValueFunc}
                renderOption={renderOption}
                getLabelFunc={getLabelFunc}
                isRequired={isRequired}
                renderTagSeparator={renderTagSeparator}
              />
            )
          : undefined
      }
      noOptionsText={emptyState}
      PaperComponent={MultiSelectPaper}
      slotProps={{
        paper: {
          value,
          options,
          onClear: () => doChange([], 'clear'),
          isRequired,
          isMultiple,
          customActionButton,
          inputToolbar,
        },
      }}
      {...autocompleteProps}
      componentsProps={{ paper: { style: { maxWidth: muiProps?.paperSize || 700 } } }}
      {...(showInput ? {} : { value: {}, disableClearable: true, sx: { ...autocompleteProps.sx, ...showOnlyButtonStyle } })}
    />
  );
}
